import { useEffect, useId, useRef, useState } from "react";
import { Menu, Phone, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { PHONE_DISPLAY, PHONE_HREF } from "./contact";

const LINKS = [
  { label: "Pallets", href: "#pallets" },
  { label: "Removal & Recycling", href: "#removal" },
  { label: "Our Story", href: "#story" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

export function Lockup({ tone = "light" }: { tone?: "light" | "dark" }) {
  return (
    <a href="#top" className="group block leading-none" aria-label="Livengood Pallet Service home">
      <span
        className={cn(
          "lockup-scale inline-block min-h-[1.15em] font-display text-2xl font-bold tracking-[0.02em] uppercase sm:text-[1.75rem]",
          tone === "light" ? "text-bone" : "text-charcoal",
        )}
        aria-hidden="true"
      >
        Livengood
      </span>
      <span className="mt-1 flex items-center gap-2" aria-hidden="true">
        <span className="hazard-rule block h-[3px] w-6 opacity-80" />
        <span
          className={cn(
            "label-eyebrow text-[0.5rem]",
            tone === "light" ? "text-bone/80" : "text-charcoal/70",
          )}
        >
          Pallet Service · Est. 1984
        </span>
      </span>
    </a>
  );
}

export function CallButton({
  className,
  label = `Call ${PHONE_DISPLAY}`,
  pulse = true,
}: {
  className?: string;
  label?: string;
  pulse?: boolean;
}) {
  return (
    <a
      href={PHONE_HREF}
      className={cn(
        // No box-shadow transitions — Lighthouse flags animated/transitioned shadows on these CTAs.
        "group inline-flex min-h-11 items-center justify-center gap-2 rounded-sm bg-amber-brand px-5 py-3 font-display text-sm font-semibold tracking-[0.12em] text-primary-foreground uppercase transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-amber-deep",
        pulse && "call-pulse",
        className,
      )}
    >
      <Phone className="h-4 w-4 handset-ring" strokeWidth={2.5} aria-hidden="true" />
      {label}
    </a>
  );
}

export function Nav() {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const openBtnRef = useRef<HTMLButtonElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    let raf = 0;
    let idleId = 0;
    let timeoutId = 0;
    let scrolled = false;
    const apply = () => {
      // Read scroll position once; only mutate classes when the boolean flips
      // so we do not invalidate styles on every scroll frame.
      const next = (window.scrollY || document.documentElement.scrollTop) > 24;
      if (next === scrolled) return;
      scrolled = next;
      bar.classList.toggle("nav-scrolled", next);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        apply();
      });
    };
    const attach = () => {
      window.addEventListener("scroll", onScroll, { passive: true });
      apply();
    };
    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(attach, { timeout: 1_200 });
    } else {
      timeoutId = window.setTimeout(attach, 1);
    }
    return () => {
      if (idleId && "cancelIdleCallback" in window) window.cancelIdleCallback(idleId);
      if (timeoutId) window.clearTimeout(timeoutId);
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    // Class toggle batches better than inline style writes during open/close.
    document.documentElement.classList.toggle("nav-lock", open);
    return () => {
      document.documentElement.classList.remove("nav-lock");
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const focusId = requestAnimationFrame(() => closeBtnRef.current?.focus());
    const menu = menuRef.current;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        openBtnRef.current?.focus();
        return;
      }
      if (e.key !== "Tab" || !menu) return;
      const focusable = menu.querySelectorAll<HTMLElement>("a[href], button:not([disabled])");
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(focusId);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="hidden bg-charcoal/95 md:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-2 font-[system-ui,sans-serif] text-[0.7rem] tracking-[0.16em] text-bone/80 uppercase">
          <span>Frankfort, Indiana · Serving Central Indiana Businesses</span>
          <a href={PHONE_HREF} className="transition-colors hover:text-amber-brand">
            Call {PHONE_DISPLAY}
          </a>
        </div>
      </div>

      <div ref={barRef} className="bg-transparent transition-all duration-500">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Lockup />

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="relative min-h-11 inline-flex items-center font-display text-xs font-medium tracking-[0.14em] text-bone/85 uppercase transition-colors hover:text-bone after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:w-0 after:bg-amber-brand after:transition-all after:duration-300 hover:after:w-full"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <CallButton className="hidden sm:inline-flex" />
            <button
              ref={openBtnRef}
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              aria-expanded={open}
              aria-controls={menuId}
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-sm border border-bone/40 p-2.5 text-bone lg:hidden"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
          className="fixed inset-0 z-50 surface-dark"
        >
          <div className="flex items-center justify-between px-6 py-5">
            <Lockup />
            <button
              ref={closeBtnRef}
              type="button"
              onClick={() => {
                setOpen(false);
                openBtnRef.current?.focus();
              }}
              aria-label="Close menu"
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-sm border border-bone/40 p-2.5 text-bone"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          <nav className="flex flex-col gap-1 px-6 pt-6" aria-label="Mobile">
            {LINKS.map((l, i) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="word-rise border-b border-bone/10 py-5 font-display text-3xl font-bold text-bone uppercase"
                style={{ animationDelay: `${80 + i * 70}ms` }}
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="absolute inset-x-0 bottom-0 border-t border-bone/10 bg-charcoal p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            <CallButton className="w-full py-4 text-base" />
          </div>
        </div>
      )}
    </header>
  );
}
